import {
  Box,
  Button,
  Collapse,
  IconButton,
  makeStyles,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import PersonPinIcon from '@material-ui/icons/PersonPin';
import { SimilarCandidatesTarget } from 'lib/Model/SimilarCandidates';
import * as React from 'react';
import { CandidateMetadataChips } from './CandidateMetadataChips';
import { ProfileSummary } from './ProfileSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2.5),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    background: '#FFFFFF',
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
  },
  iconWrap: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  eyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  candidateLinkLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  candidateLinkButton: {
    padding: 2,
    color: theme.palette.text.secondary,
    verticalAlign: 'middle',
  },
  candidateLinkIcon: {
    fontSize: 15,
  },
  candidateId: {
    fontFamily: 'monospace',
  },
  role: {
    fontWeight: 700,
  },
  meta: {
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  summaryWrap: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1.5),
    backgroundColor: '#FAFAFA',
    borderRadius: 4,
    border: '1px solid #ECECEC',
  },
  detailsToggle: {
    marginTop: theme.spacing(1),
    paddingLeft: 0,
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
  expandIcon: {
    transition: 'transform 200ms ease',
  },
  expandIconOpen: {
    transform: 'rotate(180deg)',
  },
}));

interface Props {
  candidate: SimilarCandidatesTarget;
}

function getDisplayName(candidate: SimilarCandidatesTarget): string {
  const name = [candidate.first_name, candidate.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  return name || `Candidate ${candidate.candidate_id}`;
}

function makeRecruiterFriendly(text?: string | null): string {
  if (!text) return '';

  return text
    .replace(/\bIC\b/g, 'Hands-on')
    .replace(/\bscope\b/gi, 'level of responsibility')
    .replace(/\bseniority\b/gi, 'Experience level');
}

// the applications tab, where the internal referral is created
function getAtsCandidateUrl(candidateId: string): string {
  return `https://konnekt.talexiohr.com/ats/candidates/${candidateId}#applications`;
}

export const TargetCandidateCard: React.FC<Props> = ({ candidate }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = React.useState(false);
  const profileText = makeRecruiterFriendly(candidate.profile_text);

  return (
    <Paper elevation={0} variant="outlined" className={classes.card}>
      <Box className={classes.iconWrap}>
        <PersonPinIcon />
      </Box>
      <Box className={classes.content}>
        <Typography className={classes.eyebrow}>
          Target candidate{' '}
          <Box component="span" className={classes.candidateLinkLabel}>
            <Tooltip title="Open candidate in ATS">
              <IconButton
                component="a"
                href={getAtsCandidateUrl(candidate.candidate_id)}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                className={classes.candidateLinkButton}
                aria-label={`Open candidate ${candidate.candidate_id} in ATS`}
              >
                <OpenInNewIcon className={classes.candidateLinkIcon} />
              </IconButton>
            </Tooltip>
          </Box>
        </Typography>
        <Typography variant="h6" className={classes.role}>
          {getDisplayName(candidate)}
        </Typography>
        <CandidateMetadataChips
          country={candidate.country}
          hexaco={candidate.hexaco}
          salary={candidate.salary}
          isNli={candidate.is_nli}
          tcnTypeId={candidate.tcn_type_id}
        />
        {profileText && (
          <>
            <Button
              className={classes.detailsToggle}
              size="small"
              onClick={() => setExpanded((x) => !x)}
              endIcon={
                <ExpandMoreIcon
                  className={`${classes.expandIcon} ${
                    expanded ? classes.expandIconOpen : ''
                  }`}
                />
              }
              aria-expanded={expanded}
            >
              Profile details
            </Button>
            <Collapse in={expanded} unmountOnExit>
              <Box className={classes.summaryWrap}>
                <ProfileSummary text={profileText} />
              </Box>
            </Collapse>
          </>
        )}
      </Box>
    </Paper>
  );
};
